import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_ENDPOINTS } from '../config/api';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';

interface DriveFolder {
  id: string;
  name: string;
  modifiedTime: string;
}

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  size?: string;
}

interface FolderContents {
  folders: DriveFolder[];
  files: DriveFile[];
}

interface FolderSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onFolderSelected: (folderId: string, includeSubfolders: boolean) => void;
}

type SortOption = 'name' | 'modifiedTime desc' | 'modifiedTime';

export default function FolderSelectionDialog({
  open,
  onClose,
  onFolderSelected,
}: FolderSelectionDialogProps) {
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('modifiedTime desc');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [folderPreviews, setFolderPreviews] = useState<Record<string, FolderContents>>({});
  const [loadingPreviews, setLoadingPreviews] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.drive.folders, {
        withCredentials: true,
      });
      setFolders(response.data.folders);
    } catch (err) {
      console.error('Failed to load folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderPreview = async (folderId: string) => {
    if (folderPreviews[folderId] || loadingPreviews[folderId]) {
      return;
    }

    try {
      setLoadingPreviews(prev => ({ ...prev, [folderId]: true }));
      const response = await axios.get(
        API_ENDPOINTS.drive.folderContents(folderId, sortBy),
        { withCredentials: true }
      );
      setFolderPreviews(prev => ({ ...prev, [folderId]: response.data }));
    } catch (err) {
      console.error('Failed to load folder preview:', err);
    } finally {
      setLoadingPreviews(prev => ({ ...prev, [folderId]: false }));
    }
  };

  const handleFolderClick = (folderId: string) => {
    setSelectedFolderId(folderId);
  };

  const handleConfirm = () => {
    if (selectedFolderId) {
      onFolderSelected(selectedFolderId, includeSubfolders);
    }
  };

  const sortedFolders = [...folders].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'modifiedTime':
        return new Date(a.modifiedTime).getTime() - new Date(b.modifiedTime).getTime();
      case 'modifiedTime desc':
      default:
        return new Date(b.modifiedTime).getTime() - new Date(a.modifiedTime).getTime();
    }
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Google Drive Folder</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value as SortOption)}
              >
                <MenuItem value="modifiedTime desc">Last Modified (Newest)</MenuItem>
                <MenuItem value="modifiedTime">Last Modified (Oldest)</MenuItem>
                <MenuItem value="name">Name (A-Z)</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSubfolders}
                  onChange={(e) => setIncludeSubfolders(e.target.checked)}
                />
              }
              label="Include subfolders"
            />
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : sortedFolders.length === 0 ? (
            <Alert severity="info">No folders found in your Google Drive</Alert>
          ) : (
            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {sortedFolders.map((folder) => (
                <Accordion
                  key={folder.id}
                  onChange={(_e, expanded) => {
                    if (expanded) {
                      loadFolderPreview(folder.id);
                    }
                  }}
                  sx={{
                    '&:before': { display: 'none' },
                    boxShadow: selectedFolderId === folder.id ? 2 : 0,
                    border: selectedFolderId === folder.id ? '2px solid' : '1px solid',
                    borderColor: selectedFolderId === folder.id ? 'primary.main' : 'divider',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedFolderId === folder.id}
                        onClick={() => handleFolderClick(folder.id)}
                      >
                        <ListItemIcon>
                          <FolderOpenIcon color={selectedFolderId === folder.id ? 'primary' : 'action'} />
                        </ListItemIcon>
                        <ListItemText
                          primary={folder.name}
                          secondary={`Modified: ${formatDate(folder.modifiedTime)}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  </AccordionSummary>
                  <AccordionDetails>
                    {loadingPreviews[folder.id] ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : folderPreviews[folder.id] ? (
                      <Box>
                        <Stack direction="row" spacing={1} mb={1}>
                          <Chip
                            label={`${folderPreviews[folder.id].files.length} files`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`${folderPreviews[folder.id].folders.length} subfolders`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </Stack>

                        {folderPreviews[folder.id].files.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              Files (showing first 5):
                            </Typography>
                            <List dense>
                              {folderPreviews[folder.id].files.slice(0, 5).map((file) => (
                                <ListItem key={file.id} sx={{ pl: 4 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <DescriptionIcon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={file.name}
                                    secondary={formatDate(file.modifiedTime)}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {folderPreviews[folder.id].folders.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                              Subfolders:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                              {folderPreviews[folder.id].folders.map((subfolder) => (
                                <Chip key={subfolder.id} label={subfolder.name} size="small" />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Expand to preview folder contents
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedFolderId}>
          Select Folder
        </Button>
      </DialogActions>
    </Dialog>
  );
}
